import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import { createGame, step, WIDTH, HEIGHT } from '../../lib/arcade/tetrisEngine';
import { render as renderFrame } from '../../lib/arcade/tetrisRenderer';
import { createTetrisSound } from '../../lib/arcade/tetrisSound';
import { createGameLoop } from '../../lib/arcade/gameLoop';
import ArcadeLeaderboard from './leaderboard';

/**
 * Tetris drawer UI (C). Owns one engine state, the shared fixed-step loop,
 * keyboard/swipe/button input, HUD + screens, sounds and score submission.
 */
export const GAME_KEY = 'tetris';

const HUD_RESERVED_PX = 170;
const TOUCH_CONTROLS_PX = 90;
const SWIPE_MOVE_PX = 24;
const SWIPE_DROP_PX = 40;
const TAP_MAX_MS = 250;

const HELD_KEYS = {
    ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right', ArrowDown: 'down', KeyS: 'down',
};
const ONESHOT_KEYS = {
    ArrowUp: 'rotateCw', KeyX: 'rotateCw', KeyW: 'rotateCw', KeyZ: 'rotateCcw', Space: 'hardDrop',
};
const KEY_PAUSE = ['KeyP'];
const KEY_START = ['Space', 'Enter'];

function isTouchDevice() {
    if (typeof window === 'undefined') {
        return false;
    }
    return 'ontouchstart' in window || (window.innerWidth || 0) < 768;
}

function emptyOneShot() {
    return { rotateCw: false, rotateCcw: false, hardDrop: false, moveLeft: false, moveRight: false };
}

@observer
class Tetris extends Component {
    constructor(props) {
        super(props);
        const muted = !!util.get('arcade.muted');
        this.state = {
            screen: 'title',
            score: 0,
            level: 1,
            lines: 0,
            scale: 1,
            isTouch: isTouchDevice(),
            submitting: false,
            muted,
        };
        this.sound = createTetrisSound({ muted });
        this.loop = createGameLoop({ onTick: this.tick, onFrame: this.afterFrame });
        this.game = null;
        this.held = { left: false, right: false, down: false };
        this.oneShot = emptyOneShot();
        this.frame = 0;
        this.touchStart = null;
        this.touchMoved = false;
        this.startedAt = 0;
        this.unmounted = false;
        this.canvasRef = null;
        this.wrapRef = null;
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('resize', this.fitCanvas);
        window.addEventListener('blur', this.pause);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.fitCanvas();
        this.drawIdle();
        this.loadScores();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.scale !== this.state.scale) {
            const ctx = this.getCtx();
            if (ctx && this.game) {
                renderFrame(ctx, this.game, this.frame);
            } else {
                this.drawIdle();
            }
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.loop.stop();
        this.sound.dispose();
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('resize', this.fitCanvas);
        window.removeEventListener('blur', this.pause);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    loadScores = async () => {
        const { arcadeScoreStore } = this.props.stores;
        if (arcadeScoreStore?.loadLeaderboard) {
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
    }

    getCtx() {
        return this.canvasRef ? this.canvasRef.getContext('2d') : null;
    }

    fitCanvas = () => {
        const wrapWidth = this.wrapRef ? this.wrapRef.clientWidth : WIDTH;
        const reserved = HUD_RESERVED_PX + (this.state.isTouch ? TOUCH_CONTROLS_PX : 0);
        const availableHeight = Math.max(HEIGHT, (window.innerHeight || HEIGHT) - reserved);
        const raw = Math.min(wrapWidth / WIDTH, availableHeight / HEIGHT);
        const scale = Math.max(1, Math.floor(raw * 2) / 2);
        if (scale !== this.state.scale) {
            this.setState({ scale });
        }
    }

    drawIdle() {
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, createGame(), 0);
        }
    }

    startGame = () => {
        this.sound.unlock();
        this.game = createGame();
        this.startedAt = Date.now();
        this.held = { left: false, right: false, down: false };
        this.oneShot = emptyOneShot();
        this.frame = 0;
        this.setState({
            screen: 'game', score: 0, level: 1, lines: 0, submitting: false,
        }, () => {
            if (!this.unmounted) {
                this.loop.start();
            }
        });
    }

    pause = () => {
        if (this.state.screen !== 'game') {
            return;
        }
        this.loop.stop();
        this.setState({ screen: 'paused' });
    }

    resume = () => {
        if (this.state.screen !== 'paused') {
            return;
        }
        this.sound.unlock();
        this.setState({ screen: 'game' }, () => {
            if (!this.unmounted) {
                this.loop.start();
            }
        });
    }

    togglePause = () => {
        if (this.state.screen === 'game') {
            this.pause();
        } else if (this.state.screen === 'paused') {
            this.resume();
        }
    }

    onVisibility = () => {
        if (document.hidden) {
            this.pause();
        }
    }

    close = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    toggleMute = (e) => {
        if (e && e.currentTarget && typeof e.currentTarget.blur === 'function') {
            e.currentTarget.blur();
        }
        const muted = !this.state.muted;
        this.sound.setMuted(muted);
        util.set('arcade.muted', muted);
        this.setState({ muted });
    }

    tick = (dtMs) => {
        if (!this.game) {
            return;
        }
        const input = {
            left: this.held.left || this.oneShot.moveLeft,
            right: this.held.right || this.oneShot.moveRight,
            down: this.held.down,
            rotateCw: this.oneShot.rotateCw,
            rotateCcw: this.oneShot.rotateCcw,
            hardDrop: this.oneShot.hardDrop,
        };
        this.oneShot = emptyOneShot();
        step(this.game, input, dtMs);
    }

    afterFrame = () => {
        if (!this.game) {
            return;
        }
        this.frame += 1;
        const events = this.game.events.splice(0);
        for (let i = 0; i < events.length; i += 1) {
            this.sound.play(events[i]);
        }
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, this.game, this.frame);
        }
        this.syncHud();
        if (this.game.status === 'gameOver') {
            this.loop.stop();
            this.onGameOver();
        }
    }

    syncHud() {
        const { score, level, lines } = this.game;
        if (score !== this.state.score || level !== this.state.level || lines !== this.state.lines) {
            this.setState({ score, level, lines });
        }
    }

    onGameOver = async () => {
        const { score, level } = this.game;
        const durationMs = Date.now() - this.startedAt;
        this.setState({ screen: 'gameOver', submitting: score > 0 });
        const { arcadeScoreStore } = this.props.stores;
        if (score > 0 && arcadeScoreStore?.submitScore) {
            await arcadeScoreStore.submitScore({
                game: GAME_KEY, score, wave: level, durationMs,
            });
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
        if (!this.unmounted) {
            this.setState({ submitting: false });
        }
    }

    onKeyDown = (e) => {
        const { screen } = this.state;
        const { code } = e;
        if (screen === 'game' || screen === 'paused') {
            if (HELD_KEYS[code]) {
                this.held[HELD_KEYS[code]] = true;
                e.preventDefault();
            } else if (ONESHOT_KEYS[code]) {
                if (!e.repeat) {
                    this.oneShot[ONESHOT_KEYS[code]] = true;
                }
                e.preventDefault();
            } else if (KEY_PAUSE.includes(code) && !e.repeat) {
                this.togglePause();
                e.preventDefault();
            }
            return;
        }
        if ((screen === 'title' || screen === 'gameOver') && !e.repeat && KEY_START.includes(code)) {
            e.preventDefault();
            this.startGame();
        }
    }

    onKeyUp = (e) => {
        const name = HELD_KEYS[e.code];
        if (name) {
            this.held[name] = false;
        }
    }

    touchPoint(e) {
        const touch = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
        return touch ? { x: touch.clientX, y: touch.clientY } : null;
    }

    onCanvasTouchStart = (e) => {
        if (this.state.screen !== 'game') {
            return;
        }
        e.preventDefault();
        const point = this.touchPoint(e);
        this.touchStart = point ? { ...point, t: Date.now() } : null;
        this.touchMoved = false;
    }

    onCanvasTouchMove = (e) => {
        if (this.state.screen !== 'game' || !this.touchStart) {
            return;
        }
        e.preventDefault();
        const point = this.touchPoint(e);
        if (!point) {
            return;
        }
        const dx = point.x - this.touchStart.x;
        const dy = point.y - this.touchStart.y;
        if (dy >= SWIPE_DROP_PX && Math.abs(dy) > Math.abs(dx)) {
            this.oneShot.hardDrop = true;
            this.touchMoved = true;
            this.touchStart = null;
            return;
        }
        if (Math.abs(dx) >= SWIPE_MOVE_PX) {
            if (dx > 0) {
                this.oneShot.moveRight = true;
            } else {
                this.oneShot.moveLeft = true;
            }
            this.touchMoved = true;
            // keep dragging to keep moving, one cell per SWIPE_MOVE_PX
            this.touchStart = { x: point.x, y: this.touchStart.y, t: this.touchStart.t };
        }
    }

    onCanvasTouchEnd = (e) => {
        if (this.state.screen !== 'game') {
            return;
        }
        e.preventDefault();
        if (this.touchStart && !this.touchMoved && Date.now() - this.touchStart.t <= TAP_MAX_MS) {
            this.oneShot.rotateCw = true;
        }
        this.touchStart = null;
        this.touchMoved = false;
    }

    hold = (key, value) => (e) => {
        if (e && e.cancelable) {
            e.preventDefault();
        }
        this.held[key] = value;
    }

    shoot = key => (e) => {
        if (e && e.cancelable) {
            e.preventDefault();
        }
        this.oneShot[key] = true;
    }

    renderButtons() {
        const holdBtn = (key, icon) => (
            <button
                type='button'
                class='btn btn-outline-light arcade-touch-btn'
                onTouchStart={this.hold(key, true)}
                onTouchEnd={this.hold(key, false)}
                onTouchCancel={this.hold(key, false)}
                onMouseDown={this.hold(key, true)}
                onMouseUp={this.hold(key, false)}
                onMouseLeave={this.hold(key, false)}
            >
                <i class={`fa-solid ${icon}`} />
            </button>
        );
        return (
            <div class='d-flex justify-content-between align-items-center w-100 mt-3 gap-1' style={`max-width: ${WIDTH * this.state.scale}px;`}>
                {holdBtn('left', 'fa-caret-left')}
                {holdBtn('down', 'fa-caret-down')}
                <button type='button' class='btn btn-warning arcade-touch-btn' onTouchStart={this.shoot('rotateCw')} onMouseDown={this.shoot('rotateCw')}>
                    <i class='fa-solid fa-rotate-right' />
                    <span class='visually-hidden'><Text id='arcade.rotate'>Rotate</Text></span>
                </button>
                <button type='button' class='btn btn-outline-light arcade-touch-btn' onTouchStart={this.shoot('hardDrop')} onMouseDown={this.shoot('hardDrop')}>
                    <i class='fa-solid fa-angles-down' />
                    <span class='visually-hidden'><Text id='arcade.drop'>Drop</Text></span>
                </button>
                {holdBtn('right', 'fa-caret-right')}
            </div>
        );
    }

    render() {
        const {
            screen, score, level, lines, scale, isTouch, submitting, muted,
        } = this.state;
        const { arcadeScoreStore } = this.props.stores;
        const me = arcadeScoreStore?.leaderboardGame === GAME_KEY ? arcadeScoreStore.me : arcadeScoreStore?.bestByGame?.[GAME_KEY];
        const lastSubmit = arcadeScoreStore?.lastSubmit || null;
        const best = Math.max(me?.score || 0, lastSubmit?.best?.score || 0, score);
        const cssW = `${WIDTH * scale}px`;
        const cssH = `${HEIGHT * scale}px`;
        const inGame = screen === 'game' || screen === 'paused';

        return (
            <div class='d-flex flex-column align-items-center p-3 pb-5 bg-black text-white min-vh-100' ref={c => { this.wrapRef = c; }}>
                <div class='arcade-hud d-flex justify-content-between w-100 mb-2 small' style={`max-width: ${cssW};`}>
                    <span><Text id='arcade.score'>Score</Text> {score}</span>
                    <span><Text id='arcade.level'>Level</Text> {level}</span>
                    <span><Text id='arcade.lines'>Lines</Text> {lines}</span>
                    <span><Text id='arcade.best'>Best</Text> {best}</span>
                    <button type='button' class='btn btn-sm btn-outline-light py-0 px-2 lh-1' onClick={this.toggleMute}>
                        <i class={`fa-solid ${muted ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
                        <span class='visually-hidden'>
                            {muted ? <Text id='arcade.unmute'>Unmute</Text> : <Text id='arcade.mute'>Mute</Text>}
                        </span>
                    </button>
                </div>

                <div class='arcade-screen' style={`width: ${cssW}; height: ${cssH};`}>
                    <canvas
                        ref={c => { this.canvasRef = c; }}
                        width={WIDTH}
                        height={HEIGHT}
                        class='arcade-canvas'
                        style={`width: ${cssW}; height: ${cssH};`}
                        onTouchStart={this.onCanvasTouchStart}
                        onTouchMove={this.onCanvasTouchMove}
                        onTouchEnd={this.onCanvasTouchEnd}
                    />

                    {screen === 'title' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.tetris'>Tetris</Text></h3>
                            <div class='small'>
                                {isTouch
                                    ? <Text id='arcade.controlsTetrisTouch'>Swipe to move · tap to rotate · swipe down to drop</Text>
                                    : <Text id='arcade.controlsTetrisKeyboard'>← → move · ↑/X rotate · Z rotate back · ↓ soft drop · Space hard drop · P pause</Text>}
                            </div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-info' onClick={this.startGame}>
                                    <Text id='arcade.start'>Start game</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                            <div class='small text-body-secondary'><Text id='arcade.insertCoin'>Press Space or tap Start</Text></div>
                        </div>
                    )}

                    {screen === 'paused' && (
                        <div class='arcade-overlay'>
                            <div><Text id='arcade.paused'>Paused — press P or Resume to continue</Text></div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-info btn-sm' onClick={this.resume}>
                                    <Text id='arcade.resume'>Resume</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light btn-sm' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {screen === 'gameOver' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.gameOver'>Game over</Text></h3>
                            <div><Text id='arcade.finalScore'>Final score</Text>: {score}</div>
                            {submitting && <div class='small'><Text id='arcade.savingScore'>Saving score…</Text></div>}
                            {!submitting && lastSubmit?.isNewBest && score > 0 && (
                                <div class='fw-bold'><Text id='arcade.newBest'>New personal best!</Text></div>
                            )}
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-info' onClick={this.startGame}>
                                    <Text id='arcade.playAgain'>Play again</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                                <button type='button' class='btn btn-outline-light' onClick={this.close}>
                                    <Text id='arcade.close'>Close</Text>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {isTouch && inGame && this.renderButtons()}

                {inGame && !isTouch && (
                    <div class='d-flex gap-2 mt-3'>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.togglePause}>
                            {screen === 'paused'
                                ? <Text id='arcade.resume'>Resume</Text>
                                : <Text id='arcade.pause'>Pause</Text>}
                        </button>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.close}>
                            <Text id='arcade.close'>Close</Text>
                        </button>
                    </div>
                )}

                {(screen === 'title' || screen === 'gameOver') && (
                    <div class='w-100' style={`max-width: ${cssW};`}>
                        <ArcadeLeaderboard stores={this.props.stores} game={GAME_KEY} />
                    </div>
                )}
            </div>
        );
    }
}

export default Tetris;
